/**
 * TimelineDrawer Component
 * Slide-out drawer for project timeline history
 * Opens from the right side of the screen
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import TimelineItem from './TimelineItem';
import {
  getHistoryTitle,
  getHistoryIcon,
  getHistoryColor,
  formatHistoryDescription,
  filterHistory,
  TIMELINE_FILTERS
} from '../../utils/timelineHelpers';

const ITEMS_PER_PAGE = 10;

const TimelineDrawer = ({
  isOpen,
  onClose,
  history = [],
  project,
  loading = false
}) => {
  const [filterType, setFilterType] = useState('all');
  const [displayCount, setDisplayCount] = useState(ITEMS_PER_PAGE);

  // Reset when drawer opens
  useEffect(() => {
    if (isOpen) {
      setFilterType('all');
      setDisplayCount(ITEMS_PER_PAGE);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Filter history
  const filteredHistory = filterHistory(history, filterType);
  const displayedHistory = filteredHistory.slice(0, displayCount);
  const hasMore = displayCount < filteredHistory.length;

  const drawerContent = (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 z-40 ${
          isOpen ? 'opacity-50' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[480px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-4 z-10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h2 className="text-lg font-semibold text-gray-900">Project Timeline</h2>
              <span className="text-sm text-gray-500">({filteredHistory.length} events)</span>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            {TIMELINE_FILTERS.map(filter => (
              <button
                key={filter.key}
                onClick={() => {
                  setFilterType(filter.key);
                  setDisplayCount(ITEMS_PER_PAGE);
                }}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  filterType === filter.key
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto h-[calc(100%-140px)] px-4 py-4">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <p className="mt-4 text-gray-600">Loading timeline...</p>
            </div>
          ) : displayedHistory.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No events found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {filterType !== 'all' ? 'Try a different filter' : 'No timeline events recorded yet'}
              </p>
            </div>
          ) : (
            <ul className="flow-root">
              {displayedHistory.map((item, index) => (
                <TimelineItem
                  key={item.id}
                  title={getHistoryTitle(item.changeType)}
                  description={formatHistoryDescription(item.description, item.changeType, project)}
                  timestamp={item.timestamp}
                  user={item.user}
                  icon={getHistoryIcon(item.changeType)}
                  color={getHistoryColor(item.changeType)}
                  isLast={index === displayedHistory.length - 1}
                />
              ))}
            </ul>
          )}

          {/* Load More */}
          {hasMore && !loading && (
            <div className="mt-6 text-center">
              <button
                onClick={() => setDisplayCount(prev => prev + ITEMS_PER_PAGE)}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
              >
                Load More ({filteredHistory.length - displayCount} remaining)
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );

  // Render in portal
  return createPortal(drawerContent, document.body);
};

/**
 * TimelineDrawerTrigger Component
 * Button to open the timeline drawer
 */
export const TimelineDrawerTrigger = ({ eventCount, onClick }) => {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors group"
    >
      <div className="flex items-center gap-3">
        <div className="p-2 bg-indigo-100 rounded-lg group-hover:bg-indigo-200 transition-colors">
          <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="text-left">
          <p className="text-sm font-medium text-gray-900">View Timeline</p>
          <p className="text-xs text-gray-500">{eventCount} event{eventCount !== 1 ? 's' : ''} recorded</p>
        </div>
      </div>
      <svg className="w-5 h-5 text-gray-400 group-hover:text-indigo-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
};

export default TimelineDrawer;
